/**
 * The key legend (key ?, `ui.legendOpen`): every binding from view/hotkeys.ts
 * in a compact panel at the bottom right, over the map, for learning the
 * keys without opening the ⚙ settings.
 */
import { HOTKEYS, type Hotkey } from '@/game/view/hotkeys';
import { type UiStore, useUi } from '../ui/store';

const kbd =
  'inline-grid h-4 min-w-4 place-items-center whitespace-nowrap rounded border border-line bg-fill px-1 text-[10px] font-medium tabular-nums text-fg-muted';

export function KeyLegend({ ui }: { ui: UiStore }) {
  const open = useUi(ui, (s) => s.legendOpen);
  if (!open) return null;
  return (
    <div className="panel pointer-events-auto absolute bottom-3 right-3 z-20 flex max-h-[calc(100vh-8rem)] w-72 flex-col gap-1.5 p-3 text-[11px]">
      <div className="flex items-center gap-2">
        <p className="panel-title">Keys</p>
        <kbd className={kbd}>?</kbd>
        <button
          type="button"
          aria-label="Close the key legend"
          onClick={() => ui.set({ legendOpen: false })}
          className="btn btn-ghost btn-icon ml-auto h-6 w-6 text-xs"
        >
          ✕
        </button>
      </div>
      <div className="min-h-0 overflow-y-auto">
        {HOTKEYS.flatMap((group) => group.keys).map((k) => (
          <HotkeyRow key={k.action} hotkey={k} truncate />
        ))}
      </div>
    </div>
  );
}

/** One binding: what it does, then its key caps. */
export function HotkeyRow({ hotkey, truncate = false }: { hotkey: Hotkey; truncate?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className={`text-fg-muted ${truncate ? 'truncate' : ''}`}>{hotkey.action}</span>
      <span className="flex shrink-0 gap-1">
        {hotkey.keys.map((key) => (
          <kbd key={key} className={kbd}>
            {key}
          </kbd>
        ))}
      </span>
    </div>
  );
}
